import { useTranslation } from 'react-i18next';

import { ConfirmationModal } from '@/modules/common/components/modals/confirmation-modal';

interface UnsavedChangesModalProps {
  open: boolean;
  onClose: () => void;
  onDiscard: () => void;
  isLoading?: boolean;
}

/**
 * Generic modal for confirming discard of unsaved changes
 * Used when user tries to navigate away or perform actions with dirty form
 *
 * @example
 * ```tsx
 * <UnsavedChangesModal
 *   open={showModal}
 *   onClose={() => setShowModal(false)}
 *   onDiscard={() => {
 *     // Reset form and continue with action
 *     form.reset();
 *     continueNavigation();
 *   }}
 * />
 * ```
 */
export function UnsavedChangesModal({
  open,
  onClose,
  onDiscard,
  isLoading = false,
}: UnsavedChangesModalProps) {
  const { t } = useTranslation('common');

  const handleConfirm = (closeModal: () => void) => {
    onDiscard();
    closeModal();
  };

  return (
    <ConfirmationModal
      open={open}
      onOpen={() => {}}
      onClose={onClose}
      title={t('unsavedModal.title')}
      message={t('unsavedModal.message')}
      confirmText={t('unsavedModal.discard')}
      cancelText={t('unsavedModal.cancel')}
      confirmVariant="destructive"
      onConfirm={handleConfirm}
      isLoading={isLoading}
    />
  );
}
